import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { DisplayStringTrans, useTranslation } from '@gamiphy/react-i18n';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import { LoyaltyLevel } from '../../types';
import { useLoyaltyPageContext } from '../../context';

type Props = {
  levels: LoyaltyLevel[],
  benefits: [string, ...boolean[]][],
}

const DesktopDisplay: React.FC<Props> = ({
  levels,
  benefits,
}) => {
  const { t } = useTranslation();
  const {
    cashbackProgram: {
      isCashbackProgram,
      tiersCashbackPercentages,
    },
    isCashbackEnabled,
  } = useLoyaltyPageContext();

  return (
    <>
      <Box sx={{
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.05)',
      }}
      >
        <Container maxWidth="lg">
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: `minmax(0, 1fr) ${'170px '.repeat(levels.length)}`,
            gap: '1em',
            alignItems: 'center',
            '& > div': {
              pb: 2,
            },
            '& > p': {
              pb: 2,
            },

          }}
          >
            <Typography
              fontWeight={700}
              fontSize={16}
              component="p"
              sx={{
                borderBottom: '3px solid ',
                borderColor: 'primary.main',
                width: 'fit-content',
                pr: 2,
              }}
              lineHeight="2.4rem"
            >
              {t('loyaltyPage.levels.benefits', 'Benefits')}
            </Typography>
            {levels.map((level) => (
              <Box px={4} key={level.id}>
                <Typography
                  fontWeight={700}
                  fontSize={16}
                  lineHeight="2.4rem"
                  align="center"
                  textTransform="capitalize"
                >
                  <DisplayStringTrans displayString={level.name} />
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
      <Box bgcolor="background.paper" py={5}>
        <Container maxWidth="lg">
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: `minmax(0, 1fr) ${'170px '.repeat(levels.length)}`,
            gap: '1em',
            alignItems: 'center',
          }}
          >

            <Typography variant="body2">
              {t('loyaltyPage.cashback.action', ' Make a purchase and get')}
            </Typography>
            {levels.map(({
              cash,
              id,
              tier,
            }) => (
              <Box key={id} sx={{ whiteSpace: 'nowrap' }} px={4}>
                {isCashbackProgram && isCashbackEnabled ? (
                  <>
                    <Typography sx={{
                      color: 'primary.main',
                      fontSize: 32,
                      lineHeight: '4rem',
                      fontWeight: 700,
                      textAlign: 'center',
                    }}
                    >
                      {tiersCashbackPercentages.find((item) => item.tier === tier)?.cashbackPercentage}
                      %
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'center',
                      }}
                    >
                      {t('shared.cashback', 'Cashback')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography sx={{
                      color: 'primary.main',
                      fontSize: 32,
                      lineHeight: '4rem',
                      fontWeight: 700,
                      textAlign: 'center',
                    }}
                    >
                      {Math.round(cash * 100)}
                      %
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: 'center',
                      }}
                    >
                      {t('loyaltyPage.cashback.benefit', 'Back in {{,gems}}')}
                    </Typography>
                  </>
                )}
              </Box>
            ))}
            {
              benefits.map((benefit) => (
                benefit.includes(true) && (
                <React.Fragment key={benefit[0]}>
                  <Typography variant="body2">{benefit[0]}</Typography>
                  {benefit.slice(1)
                    .map((item) => (
                      <>
                        {item && <Box textAlign="center"><CheckRoundedIcon sx={{ fontSize: 34 }} color="primary" /></Box>}
                        {!item && (
                        <Box textAlign="center">
                          <ClearRoundedIcon
                            sx={{
                              fontSize: 34,
                            }}
                            color="primary"
                          />
                        </Box>
                        )}
                      </>
                    ))}
                </React.Fragment>
                )
              ))
            }
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default DesktopDisplay;
